import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authService } from '../../services';
import { useAuth } from '../../context/AuthContext';
import Logo from '../../components/common/Logo';
import { validateEmail, validatePassword } from '../../utils/validation';

const Login = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [touched, setTouched] = useState({ email: false, password: false });
  const [fieldErrors, setFieldErrors] = useState({ email: '', password: '' });
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Check for success message from registration or info message from donation redirect
    if (location.state?.message) {
      if (location.state.from) {
        // Info message (like from donation redirect)
        setError(location.state.message);
      } else {
        // Success message (like from registration)
        setSuccess(location.state.message);
      }
      // Clear the message from location state
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  // Validate fields in real-time
  useEffect(() => {
    if (touched.email) {
      setFieldErrors(prev => ({ ...prev, email: validateEmail(email) }));
    }
  }, [email, touched.email]);

  useEffect(() => {
    if (touched.password) {
      setFieldErrors(prev => ({ ...prev, password: validatePassword(password) }));
    }
  }, [password, touched.password]);

  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Mark all fields as touched
    setTouched({ email: true, password: true });

    // Validate all fields
    const emailError = validateEmail(email);
    const passwordError = validatePassword(password);

    setFieldErrors({ email: emailError, password: passwordError });

    // If there are validation errors, don't submit
    if (emailError || passwordError) {
      return;
    }

    setLoading(true);

    try {
      const response = await authService.login({ email, password });
      // API returns: { status, statusCode, data: { accessToken, refreshToken, role, authProvider } }
      const { accessToken, refreshToken, role, authProvider } = response.data;
      const userData = {
        email,
        role: role || 'USER',
        fullName: response.data.fullName || null,
        authProvider: authProvider || null,
      };
      login(accessToken, userData, refreshToken);

      // Redirect based on role or return to previous page
      if (role === 'ADMIN' || role === 'FINANCE_ADMIN' || role === 'CONTENT_ADMIN' || role === 'SUPPORT_ADMIN') {
        navigate('/admin');
      } else if (role === 'DONOR') {
        navigate('/my-donations');
      } else if (role === 'CANDIDATE') {
        navigate('/gurukul/my-profile');
      } else {
        // If came from donation page, redirect back there
        const from = location.state?.from || '/';
        navigate(from);
      }
    } catch (err) {
      const status = err.response?.status;
      const msg = err.response?.data?.message;
      if (status === 403 && msg) {
        setError(msg);
      } else {
        setError(msg || 'Login failed. Please check your credentials.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full bg-white rounded-xl shadow-xl p-8">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <Logo size="lg" showText={false} />
          </div>
          <h2 className="text-3xl font-bold text-gray-900">{t('auth.welcomeBack')}</h2>
          <p className="mt-2 text-gray-600">{t('auth.loginToContinue')}</p>
        </div>

        {success && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg">
            {success}
          </div>
        )}

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
              {t('auth.email')} <span className="text-red-500">*</span>
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onBlur={() => handleBlur('email')}
              className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition ${touched.email && fieldErrors.email
                ? 'border-red-500 bg-red-50'
                : touched.email && !fieldErrors.email
                  ? 'border-green-500 bg-green-50'
                  : 'border-gray-300'
                }`}
              placeholder="yourname@example.com"
            />
            {touched.email && fieldErrors.email && (
              <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                {fieldErrors.email}
              </p>
            )}
            {/* {touched.email && !fieldErrors.email && (
              <p className="mt-1 text-sm text-green-600 flex items-center gap-1">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Valid email
              </p>
            )} */}
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
              {t('auth.password')} <span className="text-red-500">*</span>
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onBlur={() => handleBlur('password')}
              className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition ${touched.password && fieldErrors.password
                ? 'border-red-500 bg-red-50'
                : touched.password && !fieldErrors.password
                  ? 'border-green-500 bg-green-50'
                  : 'border-gray-300'
                }`}
              placeholder="Enter your password"
            />
            {touched.password && fieldErrors.password && (
              <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                {fieldErrors.password}
              </p>
            )}
            {/* {touched.password && !fieldErrors.password && (
              <p className="mt-1 text-sm text-green-600 flex items-center gap-1">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Valid password
              </p>
            )} */}
          </div>

          <button
            type="submit"
            disabled={loading || (touched.email && fieldErrors.email) || (touched.password && fieldErrors.password)}
            className="w-full bg-primary-600 text-white py-3 rounded-lg font-semibold hover:bg-primary-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? t('common.loading') : t('auth.signIn')}
          </button>
        </form>

        <div className="mt-4 text-center">
          <Link to="/forgot-password" className="text-sm text-primary-600 font-semibold hover:text-primary-700">
            {t('auth.forgotPassword.link')}
          </Link>
        </div>

        <div className="mt-6 text-center space-y-2">
          <p className="text-sm text-gray-600">
            {t('auth.dontHaveAccount') || "Don't have an account?"}{' '}
            <Link to="/register" className="text-primary-600 font-semibold hover:text-primary-700">
              {t('nav.register') || 'Register'}
            </Link>
          </p>
          <p className="text-sm text-gray-600">
            <Link to="/register-gurukul" className="text-primary-600 font-semibold hover:text-primary-700">
              {t('nav.registerGurukul')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
